import API_URLS from "@constants/apiUrls";
import { api } from "services/customBaseQuery";

// ============================================================================
// Types & Interfaces
// ============================================================================

/**
 * Query parameters for fetching users
 */
interface FetchUsersParams {
  page: number;
  q?: string;
  status?: string;
}

/**
 * Request body for blocking/unblocking users
 */
interface BlockUnblockUsersRequest {
  users: number[];
}

/**
 * User data structure
 */
interface User {
  id: number;
  name: string;
  email: string;
  mobile: string;
  is_active: boolean;
  submitted_on: string;
}

/**
 * Paginated users response
 */
interface FetchUsersResponse {
  data: User[];
  total_page: number;
  current_page: number;
  total_count: number;
}

/**
 * Block/Unblock users response
 */
interface BlockUnblockUsersResponse {
  message: string;
  success: boolean;
}

// ============================================================================
// API Endpoints
// ============================================================================

/**
 * User Management API
 *
 * Provides endpoints for managing users:
 * - Fetch paginated user list with search and filters
 * - Block selected users
 * - Unblock selected users
 */
export const userManagementAPI = api.injectEndpoints({
  endpoints: (build) => ({
    // ============================================================================
    // Queries
    // ============================================================================

    /**
     * Fetches paginated list of users with optional search and filter
     *
     * @param {FetchUsersParams} params - Query parameters
     * @param {number} params.page - Page number (required)
     * @param {string} [params.q] - Search term for filtering by user ID/name/email/mobile
     * @param {string} [params.status] - Activity filter (all/active/inactive)
     *
     * @returns {FetchUsersResponse} Paginated list of users
     *
     * @example
     * const { data } = useFetchUsersQuery({ page: 1, q: 'john', status: 'active' });
     */
    fetchUsers: build.query<FetchUsersResponse, FetchUsersParams>({
      query: (params) => {
        const queryString = params
          ? `?${new URLSearchParams(params as any).toString()}`
          : "";
        return {
          url: `${API_URLS.USERS}${queryString}`,
          method: "GET",
          credentials: "include",
        };
      },
      keepUnusedDataFor: 0,
      providesTags: ["Users"],
    }),

    // ============================================================================
    // Mutations
    // ============================================================================

    /**
     * Blocks selected users
     *
     * @param {BlockUnblockUsersRequest} data - Request body
     * @param {number[]} data.users - Array of user IDs to block
     *
     * @returns {BlockUnblockUsersResponse} Success/error message
     *
     * @example
     * const [blockUsers] = useBlockUsersMutation();
     * await blockUsers({ users: [1, 2, 3] }).unwrap();
     */
    blockUsers: build.mutation<
      BlockUnblockUsersResponse,
      BlockUnblockUsersRequest
    >({
      query: (data) => ({
        url: API_URLS.BLOCK_USERS,
        method: "POST",
        body: data,
        credentials: "include",
      }),
      invalidatesTags: ["Users"],
    }),

    /**
     * Unblocks selected users
     *
     * @param {BlockUnblockUsersRequest} data - Request body
     * @param {number[]} data.users - Array of user IDs to unblock
     *
     * @returns {BlockUnblockUsersResponse} Success/error message
     *
     * @example
     * const [unblockUsers] = useUnblockUsersMutation();
     * await unblockUsers({ users: [1, 2, 3] }).unwrap();
     */
    unblockUsers: build.mutation<
      BlockUnblockUsersResponse,
      BlockUnblockUsersRequest
    >({
      query: (data) => ({
        url: API_URLS.UNBLOCK_USERS,
        method: "POST",
        body: data,
        credentials: "include",
      }),
      invalidatesTags: ["Users"],
    }),
  }),
});

// ============================================================================
// Exports
// ============================================================================

/**
 * Auto-generated hooks for user management API endpoints
 */
export const {
  useFetchUsersQuery,
  useBlockUsersMutation,
  useUnblockUsersMutation,
} = userManagementAPI;

/**
 * Export types for use in components
 */
export type {
  FetchUsersParams,
  BlockUnblockUsersRequest,
  User,
  FetchUsersResponse,
  BlockUnblockUsersResponse,
};